import { Box, Button, ButtonGroup, FormControlLabel, Grid, Modal, Switch, TextField, Typography, Menu, MenuItem } from "@mui/material";
import { CustomNumberFormat } from "../../../../../components/utils/custom_number_format";
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import React, { useEffect, useMemo, useState } from "react";
import {
	CustomerList,
	DedicationList,
	DedicationRestControllerApiFactory,
	Occupation,
	OccupationPersonMonth,
	PersonList
} from "arteco-api-client-ts";
import DedicationForm from "./dedication_form";
import { useApiManager } from "../../../../../components/utils/api";
import { dedicationModalStyle } from "../../../../employee/modal";
import { fileLocalSave } from "../../../../../components/form/form_inputs/form_file";
import { colors } from "../../../../../components/utils/colors";
import { useConfirm, usePrompt } from "../../../../../components/utils/dialogs";



export interface Dedicable {
	selectedDedicationIds: number[];
	setSelectedDedicationIds: (elems: number[]) => void;
}

export interface DedicationListProps {
	/**
	 * Cambia este número para forzar una recarga. Hace falta porque crear o reparar dedicaciones
	 * no cambia ninguna de las otras props, así que la tabla se quedaba enseñando la lista vieja
	 * mientras el panel de arriba anunciaba otra cifra.
	 */
	reloadToken?: number;
	occupation: Occupation;
	year: number;
	month: string;
	showPerson: boolean;
	personId: number | undefined;
	dedicable?: Dedicable;
}

export interface DedicationSummary {
	hours: number;
	amount: number;
	availableHours: number;
	possibleHours: number;
	averageRate: number;
}


const locatePerson = (occupation: Occupation,
	month: string,
	filter: string | undefined,
	personId: number | undefined): PersonList | undefined => {

	let result: PersonList | undefined = undefined;
	const mapPerson = (person: OccupationPersonMonth): PersonList => {
		return { id: person.personId, surnamesAndName: person.personName } as PersonList;
	}
	const allPeople = (occupation.peopleByMonth ? occupation.peopleByMonth[month] : []);
	if (personId) {
		// si viene la persona, se va a piñón fijo
		const person: OccupationPersonMonth | undefined = allPeople.find(p => p.personId == personId);
		if (person) {
			result = mapPerson(person);
		}
	} else if (filter && filter.length > 0) {
		const lowerFilter = filter.toLowerCase();
		const partial = allPeople
			.map((opy) => mapPerson(opy))
			.filter(person => (person.surnamesAndName && person.surnamesAndName.toLowerCase().indexOf(lowerFilter) > -1));
		if (partial.length == 1) {
			result = partial[0];
		}
	}

	return result;
}


const DedicationTable = ({ showPerson, occupation, month, year, personId, dedicable, reloadToken }: DedicationListProps) => {
	const apiManager = useApiManager();

	const dedicationCtrl = apiManager.factory(DedicationRestControllerApiFactory);
	const { confirm, confirmDialog } = useConfirm();
	const { prompt, promptDialog } = usePrompt();

	const [summary, setSummary] = useState<DedicationSummary>({
		hours: 0,
		amount: 0,
		availableHours: 0,
		possibleHours: 0,
		averageRate: 0,
	} as DedicationSummary)

	const [filter, setFilter] = useState<string | undefined>(undefined);

	const [selectedPerson, setSelectedPerson] = useState<PersonList | undefined>(locatePerson(occupation, month, filter, personId));

	const [dedications, setDedications] = useState<DedicationList[]>([]);
	const [filteredDedication, setFilteredDedication] = useState<DedicationList[]>([]);
	const [selectedDedication, setSelectedDedication] = useState<DedicationList | undefined>(undefined);
	const [showSensibleInfo, setShowSensibleInfo] = useState<boolean>(true);
	const [showBatchForm, setShowBatchForm] = useState<boolean>(false);
	const [anchorElAutoGroup, setAnchorElAutoGroup] = useState<null | HTMLElement>(null);

	const applyAutoBillingGroup = async (mode: 'person' | 'project' | 'personId') => {
		setAnchorElAutoGroup(null);
		if (!dedicable || dedicable.selectedDedicationIds.length === 0) return;

		let entityName = "persona";
		if (mode === 'project') entityName = "proyecto";
		else if (mode === 'personId') entityName = "ID de persona";

		const prefix = await prompt({
			title: "Auto grupo de factura",
			message: `Prefijo opcional que acompañará a la ${entityName} en el texto de la factura. `
				+ "Déjalo en blanco si sólo quieres el nombre.",
			label: "Prefijo",
			defaultValue: "Servicios profesionales - "
		});
		if (prefix === undefined) return;

		const selectedDeds = dedications.filter(d => dedicable.selectedDedicationIds.includes(d.id!));
		const groups = new Map<string, number[]>();

		for (const d of selectedDeds) {
			let baseValue = "Varios";
			if (mode === 'person') {
				baseValue = d.person?.surnamesAndName || "Sin persona";
			} else if (mode === 'project') {
				baseValue = d.project || "Sin proyecto";
			} else if (mode === 'personId') {
				baseValue = d.person?.id ? `P-${d.person.id}` : "Sin persona";
			}
			const finalValue = prefix ? `${prefix.trim()} ${baseValue}` : baseValue;

			if (!groups.has(finalValue)) {
				groups.set(finalValue, []);
			}
			groups.get(finalValue)!.push(d.id!);
		}

		if (!await confirm({
			title: "Auto grupo de factura",
			message: `Se asignarán ${groups.size} grupos de factura distintos a las `
				+ `${selectedDeds.length} dedicaciones seleccionadas.`
		})) {
			return;
		}

		// Promesas asíncronas para disparar cada subgrupo a la API en bloque
		const promises = Array.from(groups.entries()).map(([billingGroup, ids]) => {
			const payload = { billingGroup } as DedicationList;
			return apiManager.execute(dedicationCtrl.updateInBatchDedication(ids, payload));
		});

		Promise.all(promises).then(() => {
			apiManager.enqueueSnackbar("Grupos de factura asignados", { variant: "success" });
			dedicable.setSelectedDedicationIds([]); // Limpiamos selección tras el éxito masivo
			loadDedication();
		});
	};

	const calcSummary = (dedication: DedicationList[]): DedicationSummary => {
		const hours: number = dedication
			.map(d => d.hours)
			.filter(d => !isNaN(d))
			.reduce((d1, d2) => d1 + d2, 0);

		const amount: number = dedication
			.map(d => d.hours * d.rate)
			.filter(d => !isNaN(d))
			.reduce((d1, d2) => d1 + d2, 0);

		const monthOccupation = occupation.planningByMonthAndPersonId ? occupation.planningByMonthAndPersonId[month] : {};
		const personRow: OccupationPersonMonth = personId && monthOccupation ? (monthOccupation as any)[personId.toString()] : undefined;

		const availableHours: number = personId
			// Ocupación de una persona
			? personRow?.availableHours || 0
			// Ocupación de todas las personas
			: Object.values(monthOccupation)
				.map((o) => o.availableHours || 0)
				.reduce((n1, n2) => n1 + n2, 0);

		const possibleHours: number = personId
			// Ocupación de una persona
			? personRow?.possibleHours || 0
			// Ocupación de todas las personas
			: Object.values(monthOccupation)
				.map((o) => o.possibleHours || 0)
				.reduce((n1, n2) => n1 + n2, 0);

		const accInitial = {
			sum: 0,
			n: 0
		};

		const accFinal = dedication.reduce((d1, d2) => {
			const hours = d2.hours;
			return { sum: d1.sum + hours * d2.rate, n: d1.n + hours };
		}, accInitial);

		return {
			hours,
			amount: amount,
			availableHours: availableHours,
			possibleHours: possibleHours,
			// Sin horas la media es 0/0: sin esta guarda el pie de tabla mostraba "NaN".
			averageRate: accFinal.n > 0 ? accFinal.sum / accFinal.n : 0
		} as DedicationSummary;
	}

	const loadDedication = () => {
		apiManager.execute(dedicationCtrl.listDedication(personId, year, parseInt(month)))
			.then(dedications => {
				setDedications(dedications);
				setFilteredDedication(dedications);
			});
	}

	useEffect(() => {
		setSelectedPerson(locatePerson(occupation, month, filter, personId));
		if (month && year) {
			loadDedication();
		}
	}, [month, year, personId, reloadToken]);

	useEffect(() => {
		setSelectedPerson(locatePerson(occupation, month, filter, personId));
	}, []);


	useEffect(() => {
		const selectedPerson = locatePerson(occupation, month, filter, personId);

		const notNullContains = (str: string | undefined, filter: string | undefined): boolean => {
			return str && filter ? str.toLowerCase().indexOf(filter.toLowerCase()) >= 0 : false;
		}

		let filtered: DedicationList[] = filter && filter.length >= 0
			? dedications.filter(d => {
				return (
					selectedPerson == undefined || selectedPerson?.id == d.person.id) && (
						notNullContains(d.customer.name, filter) ||
						notNullContains(d.customer.shortName, filter) ||
						notNullContains(d.person.surnamesAndName, filter) ||
						notNullContains(d.billingGroup, filter) ||
						notNullContains(d.personGroup, filter) ||
						notNullContains(d.project, filter) ||
						notNullContains(d.role, filter) ||
						notNullContains(d.subject, filter) ||
						notNullContains(d.manager, filter) ||
						d.rate == parseInt(filter))
			})
			: dedications;

		filtered = filtered.sort((a, b) => {
			let r = a.customer.name.localeCompare(b.customer.name);
			if (r == 0) {
				r = (a.personGroup || "").localeCompare(b.personGroup || "");
			}
			if (r == 0) {
				r = (a.budget?.title || "").localeCompare(b.budget?.title || "");
			}
			if (r == 0) {
				r = (a.manager || "").localeCompare(b.manager || "");
			}
			if (r == 0) {
				r = (a.project || "").localeCompare(b.project || "");
			}
			if (r == 0) {
				r = (a.person?.surnamesAndName || "").localeCompare(b.person?.surnamesAndName || "");
			}
			if (r == 0) {
				r = (a.id || 0) - (b.id || 0);
			}
			return r;
		})


		setFilteredDedication(filtered);
		setSelectedPerson(selectedPerson);
		const summary = calcSummary(filtered);
		setSummary(summary);

	}, [dedications, filter]);

	const [showDedicationModal, setShowDedicationModal] = useState<boolean>(false);

	// El color por proyecto se calcula una vez por lista y no dentro de cada celda: antes cada
	// fila recorría todas las dedicaciones para reconstruir el índice de proyectos, o sea O(n²).
	const colorByProject = useMemo(() => {
		const map = new Map<string, string>();
		filteredDedication.forEach(d => {
			if (d.project && !map.has(d.project)) {
				map.set(d.project, colors[map.size % colors.length]);
			}
		});
		return map;
	}, [filteredDedication]);

	const getProjectColor = (project: string | undefined): string => {
		return (project && colorByProject.get(project)) || "red";
	}

	const leftBorderWidth = 5;

	function getFilteredIds() {
		return filteredDedication
			.map(d => d.id as number)
			.filter(d => d != undefined);
	}

	return (
		<>
			{confirmDialog}
			{promptDialog}
			<Box my={3}>
				<Alert sx={{ marginY: 3 }} severity="info">
					<AlertTitle>Resumen mensual</AlertTitle>
					<Grid container spacing={3}>
						<Grid item>
							<b>REAL</b><br />
							Horas: <CustomNumberFormat amount={summary.hours} /> <br />
							Importe: <CustomNumberFormat amount={summary.amount} />
						</Grid>
						<Grid item>
							<b>TEÓRICO</b><br />
							Horas posibles: <CustomNumberFormat amount={summary.possibleHours} /> &nbsp;
							{selectedPerson ? "personal" : "todas"}
							<br />
							Horas asistentes: <b><CustomNumberFormat amount={summary.availableHours} /></b> &nbsp;
							{selectedPerson ? "personal" : "todas"}
							<br />
							{/* Verde = no se factura por debajo de lo disponible. Antes exigía superarlo
							    en 0.1, así que cuadrar exacto salía en rojo: el caso bueno parecía el malo. */}
							Diff: <CustomNumberFormat
								style={{ color: (summary.hours - summary.availableHours) > -0.1 ? "green" : "red" }}
								amount={summary.hours - summary.availableHours} />
						</Grid>
					</Grid>
				</Alert>


				<Grid container spacing={3}>
					<Grid item>
						<Button variant={"contained"}
							title={"Validar dedicaciones seleccionadas"}
							disabled={(dedicable?.selectedDedicationIds.length || 0) < 1}
							onClick={async _ => {
								if (dedicable &&
									dedicable.selectedDedicationIds.length > 0 &&
									await confirm({
										title: "Validar dedicaciones",
										message: `Se cambiará el estado de validación de las ${dedicable.selectedDedicationIds.length} dedicaciones seleccionadas.`
									})) {
									apiManager
										.execute(dedicationCtrl.validateInBatchDedication(dedicable.selectedDedicationIds))
										.then(_ => {
											dedicable.setSelectedDedicationIds([]);
											loadDedication();
										})
								}
							}}>
							Validar
						</Button>
						<Button sx={{ marginLeft: "1em" }} variant={"contained"}
							title={"Editar dedicaciones seleccionadas"}
							disabled={(dedicable?.selectedDedicationIds.length || 0) < 1}
							onClick={_ => setShowBatchForm(true)}>
							Editar lote
						</Button>

						<Button sx={{ marginLeft: "1em" }} variant={"contained"} color={"secondary"}
							title={"Generar y asignar de forma automatizada grupos de factura a la selección actual"}
							disabled={(dedicable?.selectedDedicationIds.length || 0) < 1}
							onClick={(e) => setAnchorElAutoGroup(e.currentTarget)}>
							Auto G.Fra
						</Button>
						<Menu
							anchorEl={anchorElAutoGroup}
							open={Boolean(anchorElAutoGroup)}
							onClose={() => setAnchorElAutoGroup(null)}
						>
							<MenuItem onClick={() => applyAutoBillingGroup('person')}>Mapear por Persona</MenuItem>
							<MenuItem onClick={() => applyAutoBillingGroup('project')}>Mapear por Proyecto</MenuItem>
							<MenuItem onClick={() => applyAutoBillingGroup('personId')}>Mapear por {"P-<id_persona>"}</MenuItem>
						</Menu>
						<Button sx={{ marginLeft: "1em" }} variant={"contained"}
							title={filteredDedication.length !== dedications.length ? "Debe limpiar el filtro de búsqueda para poder exportar a XLS" : "Exportar todas las dedicaciones a XLS"}
							disabled={dedications.length < 1 || filteredDedication.length !== dedications.length}
							onClick={_ => {
								const allIds = dedications.map(d => d.id as number).filter(id => id !== undefined);
								apiManager
									.executeRaw(dedicationCtrl.exportXlsDedication(allIds))
									.then((resp) => fileLocalSave(resp));
							}}>
							Xls
						</Button>
						<Button sx={{ marginLeft: "1em" }} variant={"contained"}
							onClick={_ => {
								setSelectedDedication({
									year: new Date().getFullYear(),
									month: new Date().getMonth()
								} as DedicationList);
								setShowDedicationModal(true);
							}}>
							Nueva
						</Button>
					</Grid>

					<Grid item sx={{ flexGrow: 1 }}>
						<FormControlLabel control={<Switch checked={showSensibleInfo}
							onChange={e => setShowSensibleInfo(!showSensibleInfo)} />}
							label="Importes" />
					</Grid>

					<Grid item>
						<TextField value={filter} label="Filtro" variant="outlined" size={"small"}
							sx={{ width: "10em;" }}
							onChange={(e) => {
								const value = e.target.value;
								if (value != "") {
									setFilter(e.target.value);
								} else {
									setFilter(undefined);
								}
							}} />
					</Grid>
				</Grid>

			</Box>
			{dedicable && (
				<Box sx={{ py: 2 }}>
					<ButtonGroup size={"small"}>
						<Button onClick={_ => {
							const current = new Set(dedicable.selectedDedicationIds);
							filteredDedication.forEach(d => {
								if (d.id !== undefined) current.add(d.id);
							});
							dedicable.setSelectedDedicationIds(Array.from(current));
						}}>todas</Button>
						<Button onClick={_ => {
							const current = new Set(dedicable.selectedDedicationIds);
							filteredDedication.forEach(d => {
								if (d.id !== undefined) {
									if (current.has(d.id)) {
										current.delete(d.id);
									} else {
										current.add(d.id);
									}
								}
							});
							dedicable.setSelectedDedicationIds(Array.from(current));
						}}>invertir</Button>
						<Button onClick={_ => {
							dedicable.setSelectedDedicationIds([]);
						}}>ninguna</Button>
					</ButtonGroup>
				</Box>
			)}
			{/* Tabla densa: en móvil se desplaza dentro de su caja, no la página (#13893). */}
			<Box sx={{overflowX: "auto", maxWidth: "100%"}}>
			<table className={"table-dedication"}>
				<thead>
					<tr>
						<th>{dedicable && dedicable.selectedDedicationIds.length ? dedicable.selectedDedicationIds.length : ""}</th>
						<th align={"left"}>CLIENTE</th>
						<th align={"left"}>PRESUP.</th>
						<th align={"left"}>PROJECT</th>
						<th align={"left"}>MANAGER</th>
						{showPerson && (
							<th align={"left"}>PERSONA</th>
						)}
						{showSensibleInfo && (
							<th align={"left"}>ROL</th>
						)}
						{showSensibleInfo && (
							<th align={"right"}>TARIFA</th>
						)}
						<th align={"right"}>HORAS</th>
						{showSensibleInfo && (
							<th align={"left"}>VALIDADO</th>
						)}
						{showSensibleInfo && (
							<th align={"left"}>FACT.</th>
						)}

						<th align={"left"}>PER.GRP.</th>

						{showSensibleInfo && (
							<th align={"left"} title={"Grupo de Factura"}>G. FACTURA</th>
						)}
						<th align={"left"}>CONCEPTO</th>
						{showSensibleInfo && (
							<th align={"right"}>IMP. SIN IVA</th>
						)}
					</tr>
				</thead>
				<tbody>
					{filteredDedication && filteredDedication.map((elem, i) => {
						const isSelected = dedicable?.selectedDedicationIds.includes(elem.id as number);
						return (
							<tr key={elem.id || i}
								title={elem.billLineId ? "Facturado" : undefined}
								style={{
									backgroundColor: isSelected ? "#fff9c4" : undefined,
								}}>
								<td style={{ backgroundColor: getProjectColor(elem.project) }}>

									{dedicable && (
										<input type={"checkbox"}
											value={elem.id}
											className={"dedi-check"}
											checked={isSelected}
											onChange={(e) => {
												const id = elem.id as number;
												if (e.target.checked) {
													if (!dedicable.selectedDedicationIds.find(d => d == id)) {
														const newElems = [...dedicable.selectedDedicationIds, id];
														dedicable.setSelectedDedicationIds(newElems);
													}
												} else {
													const elems = dedicable.selectedDedicationIds.filter(d => d != id);
													dedicable.setSelectedDedicationIds(elems);
												}
											}
											} />
									)}

								</td>
								<td>
									<span style={{
										cursor: "pointer",
										border: "1px solid gray",
										padding: "3px 10px",
										backgroundColor: "lightgray"
									}}
										onClick={() => {
											setSelectedDedication(elem);
											setShowDedicationModal(true);
										}}>
										{(elem.customer?.shortName || "").substring(0, 12)}
									</span>
								</td>
								<td>{elem.budget?.title}</td>
								<td>{elem.project}</td>
								<td>{elem.manager}</td>
								{showPerson && (
									<td title={elem.role}>{elem.person.surnamesAndName}</td>
								)}
								{showSensibleInfo && (
									<td align={"left"}>{elem.role || ""}</td>
								)}
								{showSensibleInfo && (
									<td align={"right"}><CustomNumberFormat amount={elem.rate} /></td>
								)}
								<td align={"right"}><CustomNumberFormat amount={elem.hours} scale={1} /></td>
								{showSensibleInfo && (
									<td>{elem.validated ? "sí" : ""}</td>
								)}
								{showSensibleInfo && (
									<td>{elem.billNum}</td>
								)}
								{showSensibleInfo && (
									<td>{elem.personGroup}</td>
								)}

								<td>{elem.billingGroup}</td>

								<td align={"left"}>{elem.subject}</td>
								{showSensibleInfo && (
									<td align={"right"}>
										<CustomNumberFormat amount={elem.hours * elem.rate} />
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
				<tfoot>
					<tr style={{ borderLeftColor: "gray", borderLeftWidth: leftBorderWidth, borderLeftStyle: "solid" }}>
						<th></th>
						<th></th>
						<th></th>
						<th></th>
						<th></th>
						{showPerson && (
							<th></th>
						)}
						{showSensibleInfo && (
							<th>{/* rol */}</th>
						)}
						{showSensibleInfo && (
							<th align={"right"}>{summary.averageRate?.toFixed(1)}</th>
						)}
						<th align={"right"}>{summary.hours.toFixed(1)}</th>
						{showSensibleInfo && (
							<th>{/* validated */}</th>
						)}
						{showSensibleInfo && (
							<th>{/* validated */}</th>
						)}

						<th>{/* person group */}</th>

						{showSensibleInfo && (
							<th>{/* billing group */}</th>
						)}
						<th>{/* subject */}</th>
						{showSensibleInfo && (
							<th align={"right"}><CustomNumberFormat amount={summary.amount} /></th>
						)}
					</tr>

				</tfoot>
			</table>
			</Box>

			<Modal
				open={showDedicationModal}
				onClose={_ => setShowDedicationModal(false)}>
				<Box sx={dedicationModalStyle}>
					<Typography sx={{ mt: 2 }}>
						<DedicationForm dedication={selectedDedication}
							year={year} month={parseInt(month)}
							summary={selectedPerson ? summary : undefined}
							person={selectedPerson}
							onChange={d => {
								setSelectedDedication(d);
								loadDedication();
								setShowDedicationModal(false);
							}} />
					</Typography>
				</Box>
			</Modal>

			<Modal
				open={showBatchForm}
				onClose={_ => setShowBatchForm(false)}>
				<Box sx={dedicationModalStyle}>
					<Typography sx={{ mt: 2 }}>
						<DedicationForm batch={true}
							dedication={undefined}
							year={year}
							month={parseInt(month)}
							onChange={d => {
								apiManager
									.execute(dedicationCtrl.updateInBatchDedication(dedicable?.selectedDedicationIds || [], d as DedicationList))
									.then(resp => {
										apiManager.enqueueSnackbar("Se han actualizado " + resp + " dedicaciones", { variant: "success" });
										setShowBatchForm(false);
										loadDedication();
									});
							}} />
					</Typography>
				</Box>
			</Modal>


		</>


	);
}

export default DedicationTable;